// Site configuration — Spokane Gutter Co.
// SEO: URL slugs, H1s, titles, and meta descriptions all lead with the
// exact-match keyword phrase (e.g. "Spokane Gutter Installation").

export const BUSINESS = {
  name: "Spokane Gutter Co.",
  legalName: "Spokane Gutter Co.",
  phone: "(509) 608-1208",
  phoneHref: "tel:+15096081208",
  email: "hello@spokanegutter.org",
  emailHref: "mailto:hello@spokanegutter.org",
  street: "",
  addressLocality: "Spokane",
  addressRegion: "WA",
  postalCode: "99201",
  areaServed: [
    "Spokane, WA",
    "Spokane Valley, WA",
    "Liberty Lake, WA",
    "Mead, WA",
    "Cheney, WA",
    "Nine Mile Falls, WA",
  ],
  hours: [
    { day: "Monday", open: "07:00", close: "18:00" },
    { day: "Tuesday", open: "07:00", close: "18:00" },
    { day: "Wednesday", open: "07:00", close: "18:00" },
    { day: "Thursday", open: "07:00", close: "18:00" },
    { day: "Friday", open: "07:00", close: "18:00" },
    { day: "Saturday", open: "08:00", close: "14:00" },
    { day: "Sunday", open: "Closed", close: "" },
  ],
  founded: 2014,
  siteUrl: "https://spokanegutter.org",
  socials: { facebook: "", instagram: "" },
};

// -----------------------------------------------------------------------
// SERVICES — flat URL slug `/gutter-installation/`
// Each targets a broad, non-city keyword ("gutter installation", "roofing").
// -----------------------------------------------------------------------
export const SERVICES = [
  {
    slug: "gutter-installation",
    name: "Gutter Installation",
    keyword: "gutter installation",
    short: "Seamless K-style and half round gutters, cut on site to your roof line.",
    intro:
      "New seamless gutters, cut and hung on the same day. We measure your roof line, roll the profile on site in copper, aluminum, or steel, and set every hanger every 24 inches so nothing sags after the first heavy snow.",
    bullets: [
      "5 inch and 6 inch K-style seamless",
      "Half round and box style options",
      "32 gauge aluminum, copper, or galvalume steel",
      "Hidden hangers every 24 inches",
      "Downspouts sized to your roof pitch and area",
    ],
    process: [
      "Free on site measure and pitch check",
      "Same day roll and hang from a mobile machine",
      "Sealed miters and downspout runs",
      "Water test and cleanup before we leave",
    ],
    title: "Gutter Installation in Spokane WA | Seamless, Same Day",
    meta: "Seamless gutter installation across the Spokane area. K-style or half round, cut on site, hung the same day, hidden hangers every 24 inches. Free on site quote.",
    accent: "01",
  },
  {
    slug: "gutter-repair",
    name: "Gutter Repair",
    keyword: "gutter repair",
    short: "Sagging runs, leaking miters, and pulled hangers fixed in a single visit.",
    intro:
      "If a section pulled off in a windstorm, a miter is leaking behind the fascia, or the pitch is wrong, we fix the actual cause instead of taping over it. Most repairs take two hours or less.",
    bullets: [
      "Re-hang sagging runs with new hidden hangers",
      "Reseal leaking miters and end caps",
      "Fascia and soffit board repair",
      "Downspout replacement and re-routing",
      "Storm damage assessments in writing",
    ],
    process: [
      "Ladder walk and photo report",
      "Written scope with a flat price",
      "Same day fix on most single story homes",
      "One year workmanship warranty",
    ],
    title: "Gutter Repair in Spokane WA | Sagging, Leaks, Storm Damage",
    meta: "Gutter repair across the Spokane area. Sagging runs, leaking miters, pulled hangers, and storm damage fixed in a single visit. Flat price. One year warranty.",
    accent: "02",
  },
  {
    slug: "gutter-cleaning",
    name: "Gutter Cleaning",
    keyword: "gutter cleaning",
    short: "Hand cleaned, flushed, and photographed. No leaf blowers into your bushes.",
    intro:
      "Every gutter is cleared by hand, bagged, and hauled off. We flush every downspout with a garden hose and send you before and after photos when the truck pulls away.",
    bullets: [
      "Full hand clean, no blowers in flower beds",
      "Downspout flush and clog clear",
      "Debris hauled off site",
      "Before and after photos to your phone",
      "Optional inspection report on hanger condition",
    ],
    process: [
      "Two person crew for safety",
      "Ladder standoffs to protect your gutters",
      "Downspout hose flush test",
      "Yard blown clean before we leave",
    ],
    title: "Gutter Cleaning in Spokane WA | Hand Cleaned and Flushed",
    meta: "Gutter cleaning across the Spokane area. Every run hand cleared, bagged, and hauled. Downspouts flushed. Before and after photos texted to your phone.",
    accent: "03",
  },
  {
    slug: "gutter-guards",
    name: "Gutter Guards",
    keyword: "gutter guards",
    short: "Micro mesh guards that keep pine needles and shingle grit out of your runs.",
    intro:
      "Pine needles are the problem in Spokane. We install stainless micro mesh at the correct roof pitch so needles shed instead of pack. No plastic screens that warp in July.",
    bullets: [
      "Stainless micro mesh at .020 opening",
      "Sized to shed pine needles and shingle grit",
      "Pitch tuned to your roof for water flow",
      "Works with existing 5 and 6 inch gutters",
      "Lifetime product warranty",
    ],
    process: [
      "On site measure and pine load check",
      "Roll cut lengths to your run",
      "Screw fastened, not clipped",
      "Cleared and flushed before install",
    ],
    title: "Gutter Guards in Spokane WA | Pine Needle Proof Micro Mesh",
    meta: "Stainless micro mesh gutter guards across the Spokane area. Sheds pine needles instead of packing them. Screw fastened, not clipped. Lifetime product warranty.",
    accent: "04",
  },
  {
    slug: "roofing",
    name: "Roofing",
    keyword: "roofing",
    short: "Architectural asphalt and standing seam metal, built for Inland Northwest snow load.",
    intro:
      "We install architectural asphalt shingles and standing seam metal roofs that are engineered for the snow load Spokane throws at them. Every tear off is dry in a day so weather is never a risk.",
    bullets: [
      "Architectural asphalt with 130 mph wind rating",
      "Standing seam metal in 24 gauge",
      "Ice and water shield on eaves and valleys",
      "Ridge vent and soffit vent balanced",
      "Manufacturer backed material warranty",
    ],
    process: [
      "Free roof inspection with drone photos",
      "Written scope with a flat price",
      "Tear off and dry in same day",
      "Full cleanup with magnetic nail sweep",
    ],
    title: "Roofing Contractor in Spokane WA | Asphalt and Standing Seam",
    meta: "Roofing contractor across the Spokane area. Architectural asphalt and 24 gauge standing seam metal. Built for Inland Northwest snow load. Free drone inspection.",
    accent: "05",
  },
];

// -----------------------------------------------------------------------
// CITIES — flat URL slug `/spokane/`, `/liberty-lake/`
// Each targets `[city] gutters` / `[city] roofing contractor`.
// -----------------------------------------------------------------------
export const CITIES = [
  {
    slug: "spokane",
    name: "Spokane",
    keyword: "spokane gutter contractor",
    subtitle: "Downtown, South Hill, Five Mile, and every zip in between.",
    intro:
      "Spokane runs from the basalt bluffs of the South Hill to the pines above Five Mile. Every neighborhood has its own quirk. On the South Hill it is 100 year old craftsman fascia that needs careful hanger placement. In Kendall Yards it is modern flat lines that want half round in bronze. Downtown it is commercial box gutter work.",
    weatherAngle:
      "Snow load and ice dams are the story in Spokane. We hang gutters with heavier hidden hangers every 24 inches and pitch runs a touch steeper so freeze and thaw does not lift the front lip.",
    neighborhoods: ["South Hill", "Kendall Yards", "Five Mile", "North Hill", "Comstock", "Manito", "Peaceful Valley", "West Central"],
    zip: "99201",
    lat: 47.6588,
    lng: -117.4260,
    accent: "SP",
    title: "Spokane Gutter Contractor | Seamless Gutters and Roofing",
    meta: "Gutter and roofing contractor serving Spokane WA. Seamless installation, repair, cleaning, guards, and roofing. Local crew. Same day quotes on the South Hill and beyond.",
  },
  {
    slug: "spokane-valley",
    name: "Spokane Valley",
    keyword: "spokane valley gutters",
    subtitle: "From Millwood to Greenacres, seamless work for Valley homes.",
    intro:
      "Spokane Valley homes sit on wider lots with longer roof lines. Most single story ranches here want 6 inch K-style with 3x4 downspouts because runoff volume is higher than a compact city lot. We roll on site so a 90 foot run is one continuous piece.",
    weatherAngle:
      "The Valley catches wind off the Rathdrum Prairie. We fasten hangers on 24 inch centers minimum and use screw fastened downspout straps so nothing flaps loose in a February gust.",
    neighborhoods: ["Millwood", "Opportunity", "Greenacres", "Ponderosa", "Veradale", "University"],
    zip: "99206",
    lat: 47.6733,
    lng: -117.2394,
    accent: "SV",
    title: "Spokane Valley Gutters and Roofing | Millwood to Greenacres",
    meta: "Gutters and roofing in Spokane Valley WA. Seamless K-style on wide lot roofs. 6 inch runs with 3x4 downspouts for high volume runoff. Local Valley crew.",
  },
  {
    slug: "liberty-lake",
    name: "Liberty Lake",
    keyword: "liberty lake gutters",
    subtitle: "Lakeside and River District homes with clean modern lines.",
    intro:
      "Liberty Lake is a mix of lakeside cabins that have been rebuilt into full time homes and new construction near the River District. Both want quiet, precise gutter work. We use hidden hangers, painted downspouts, and half round runs when the architecture calls for it.",
    weatherAngle:
      "Homes near the water get more debris load from cottonwoods and pines. Micro mesh guards paired with a fall clean keeps runs open through the freeze season.",
    neighborhoods: ["Liberty Lake shoreline", "River District", "MeadowWood", "Legacy Ridge"],
    zip: "99019",
    lat: 47.6699,
    lng: -117.0898,
    accent: "LL",
    title: "Liberty Lake Gutters and Roofing | Lakeside and River District",
    meta: "Gutters and roofing in Liberty Lake WA. Half round, painted downspouts, and micro mesh guards for lakeside and River District homes. Quiet, precise gutter work.",
  },
  {
    slug: "mead",
    name: "Mead",
    keyword: "mead gutters",
    subtitle: "North side homes surrounded by ponderosa pine.",
    intro:
      "Mead is pine country. Every roof up here catches needles by the pound between August and November. We build gutter systems around that fact. Guards go on at install, not as an add on later.",
    weatherAngle:
      "Ponderosa needles clog standard screens in one season. We spec .020 stainless micro mesh so the needles shed off the roof line instead of packing the gutter throat.",
    neighborhoods: ["Mead", "Green Bluff", "Colbert edge", "North Mead"],
    zip: "99021",
    lat: 47.7666,
    lng: -117.3547,
    accent: "MD",
    title: "Mead WA Gutters and Roofing | Pine Needle Country",
    meta: "Gutters and roofing in Mead WA. Pine country needs stainless .020 micro mesh guards installed at the right pitch. Local crew serving Green Bluff and Colbert.",
  },
  {
    slug: "cheney",
    name: "Cheney",
    keyword: "cheney gutters",
    subtitle: "College town homes and rural rebuilds south of Spokane.",
    intro:
      "Cheney gets everything Spokane gets plus more open wind. Older homes near EWU tend to have short eaves and steep pitches. We size downspouts up to 3x4 and add splash blocks so the foundation stays dry through spring melt.",
    weatherAngle:
      "Wind off the Palouse hits Cheney hard. We use heavier gauge downspout straps and screw them into stud line, not just siding.",
    neighborhoods: ["Downtown Cheney", "EWU area", "Betz", "Salnave"],
    zip: "99004",
    lat: 47.4874,
    lng: -117.5758,
    accent: "CH",
    title: "Cheney WA Gutters and Roofing | Wind Rated Downspouts",
    meta: "Gutters and roofing in Cheney WA. Wind off the Palouse means heavier straps and stud line fastening. Serving EWU rentals, farm rebuilds, and Downtown Cheney.",
  },
  {
    slug: "nine-mile-falls",
    name: "Nine Mile Falls",
    keyword: "nine mile falls gutters",
    subtitle: "Rural properties along the Spokane River and Long Lake.",
    intro:
      "Nine Mile Falls is trees and long driveways. Most homes here have multiple roof planes, dormers, and porch runs. We design a gutter system that keeps water off the deck posts and away from crawl space vents.",
    weatherAngle:
      "Snow slides off metal roofs up here in sheets. We install snow guards above every gutter run so the hangers do not tear off in the first thaw.",
    neighborhoods: ["Nine Mile Falls", "Suncrest", "Long Lake shoreline", "Tumtum edge"],
    zip: "99026",
    lat: 47.7815,
    lng: -117.5687,
    accent: "NM",
    title: "Nine Mile Falls WA Gutters and Roofing | Rural Snow Country",
    meta: "Gutters and roofing in Nine Mile Falls WA. Long roof runs, dormers, and snow guards on metal roofs. Local crew serving Suncrest, Long Lake, and Tumtum.",
  },
];

// -----------------------------------------------------------------------
// SERVICE × CITY MATRIX — 30 flat URL slugs `/spokane-gutter-installation/`
// Every combo has: unique title, unique meta (>140 chars), unique H1,
// unique intro paragraph. Keyword is the URL slug itself.
// -----------------------------------------------------------------------
export function scSlug(citySlug, serviceSlug) {
  return `${citySlug}-${serviceSlug}`;
}

// Compact per-combo overrides. Each is meant to READ NATURAL and target the
// exact keyword phrase "[city] [service]".
export const SC_META = {
  // ------ GUTTER INSTALLATION ------
  "spokane-gutter-installation": {
    title: "Spokane Gutter Installation | Seamless, Same Day, On Site",
    meta: "Spokane gutter installation. Seamless K-style rolled on your driveway and hung the same day. Hidden hangers every 24 inches. Free on site quote inside city limits.",
    h1: "Spokane Gutter Installation.",
    hook: "Cut on your driveway. Hung the same day.",
  },
  "spokane-valley-gutter-installation": {
    title: "Spokane Valley Gutter Installation | 6 Inch Seamless on Wide Lots",
    meta: "Spokane Valley gutter installation. 6 inch K-style with 3x4 downspouts for high volume Valley runoff. Screw fastened straps rated for Palouse wind gusts.",
    h1: "Spokane Valley Gutter Installation.",
    hook: "6 inch K-style for wide lot runoff.",
  },
  "liberty-lake-gutter-installation": {
    title: "Liberty Lake Gutter Installation | Half Round and Painted",
    meta: "Liberty Lake gutter installation. Half round runs and color matched downspouts for lakeside and River District homes. Clean modern lines, quiet install.",
    h1: "Liberty Lake Gutter Installation.",
    hook: "Half round runs on modern lake homes.",
  },
  "mead-gutter-installation": {
    title: "Mead Gutter Installation | Built for Pine Needle Country",
    meta: "Mead gutter installation. Seamless runs sized for high pine needle load. Micro mesh guards installed the same day so needles never enter the system.",
    h1: "Mead Gutter Installation.",
    hook: "Seamless runs with guards on install day.",
  },
  "cheney-gutter-installation": {
    title: "Cheney Gutter Installation | Wind Rated Straps, 3x4 Downspouts",
    meta: "Cheney gutter installation. Palouse wind means stud line fastening and 3x4 downspouts. Serving EWU rentals, farm rebuilds, and Downtown Cheney homes.",
    h1: "Cheney Gutter Installation.",
    hook: "Stud line fastening. Palouse wind rated.",
  },
  "nine-mile-falls-gutter-installation": {
    title: "Nine Mile Falls Gutter Installation | Snow Guard Ready",
    meta: "Nine Mile Falls gutter installation. Long rural roof lines, dormers, porch tie ins, and snow guards over every metal run. Serving Suncrest and Long Lake.",
    h1: "Nine Mile Falls Gutter Installation.",
    hook: "Long rural runs. Snow guard equipped.",
  },
  // ------ GUTTER REPAIR ------
  "spokane-gutter-repair": {
    title: "Spokane Gutter Repair | Miters, Hangers, Storm Damage",
    meta: "Spokane gutter repair. Leaking miters, pulled hangers, sagging runs, and storm damage fixed in one visit. Flat price on site. One year workmanship warranty.",
    h1: "Spokane Gutter Repair.",
    hook: "Fixed in one visit. Flat price.",
  },
  "spokane-valley-gutter-repair": {
    title: "Spokane Valley Gutter Repair | Same Day Fixes",
    meta: "Spokane Valley gutter repair. Sagging 6 inch runs re-hung, downspout elbows replaced, storm damage documented for insurance. Same day on most Valley homes.",
    h1: "Spokane Valley Gutter Repair.",
    hook: "Same day fixes across the Valley.",
  },
  "liberty-lake-gutter-repair": {
    title: "Liberty Lake Gutter Repair | Miter Reseal, Downspout Swap",
    meta: "Liberty Lake gutter repair. Miter reseals, downspout swaps, and half round hanger replacement. Serving lakeside and River District homes.",
    h1: "Liberty Lake Gutter Repair.",
    hook: "Miter reseal and hanger reset.",
  },
  "mead-gutter-repair": {
    title: "Mead Gutter Repair | Needle Damage and Downspout Clogs",
    meta: "Mead gutter repair. Pine needle overflow damage, downspout clogs, and re-pitch after a heavy snow year. Serving Green Bluff, Colbert edge, and North Mead.",
    h1: "Mead Gutter Repair.",
    hook: "Needle damage and downspout unclog.",
  },
  "cheney-gutter-repair": {
    title: "Cheney Gutter Repair | Wind Damage and Loose Straps",
    meta: "Cheney gutter repair. Loose downspout straps, wind bent runs, and storm damage assessments in writing. Serving Downtown Cheney and the EWU area.",
    h1: "Cheney Gutter Repair.",
    hook: "Wind damage and loose strap fix.",
  },
  "nine-mile-falls-gutter-repair": {
    title: "Nine Mile Falls Gutter Repair | Snow Load and Ice Damage",
    meta: "Nine Mile Falls gutter repair. Snow torn hangers, ice damage on eaves, and porch tie in leaks fixed the same visit. Serving Suncrest and Long Lake.",
    h1: "Nine Mile Falls Gutter Repair.",
    hook: "Snow torn hangers and eave leaks.",
  },
  // ------ GUTTER CLEANING ------
  "spokane-gutter-cleaning": {
    title: "Spokane Gutter Cleaning | Hand Cleared, Downspouts Flushed",
    meta: "Spokane gutter cleaning. Every run hand cleared, bagged, and hauled. Downspouts flushed with a garden hose. Before and after photos texted before we leave.",
    h1: "Spokane Gutter Cleaning.",
    hook: "Hand cleared. Photo report to your phone.",
  },
  "spokane-valley-gutter-cleaning": {
    title: "Spokane Valley Gutter Cleaning | Long Ranch Runs, Hand Cleared",
    meta: "Spokane Valley gutter cleaning. Long ranch runs cleared by hand, downspouts flushed, debris hauled off site. Serving Millwood, Ponderosa, and Greenacres.",
    h1: "Spokane Valley Gutter Cleaning.",
    hook: "Long ranch runs cleared by hand.",
  },
  "liberty-lake-gutter-cleaning": {
    title: "Liberty Lake Gutter Cleaning | Cottonwood and Pine Debris",
    meta: "Liberty Lake gutter cleaning. Cottonwood fluff, pine needles, and shingle grit cleared by hand. Downspouts flushed. Ideal before or after freeze season.",
    h1: "Liberty Lake Gutter Cleaning.",
    hook: "Cottonwood and pine debris clear.",
  },
  "mead-gutter-cleaning": {
    title: "Mead Gutter Cleaning | Ponderosa Needle Clear Out",
    meta: "Mead gutter cleaning. Ponderosa needles by the pound, cleared by hand, bagged, and hauled off. Downspout hose flush test on every run.",
    h1: "Mead Gutter Cleaning.",
    hook: "Ponderosa needle clear out by hand.",
  },
  "cheney-gutter-cleaning": {
    title: "Cheney Gutter Cleaning | Wind Blown Debris and Flush",
    meta: "Cheney gutter cleaning. Wind blown Palouse debris, roof grit, and organic material cleared. Downspout flush test. Serving EWU area and Downtown Cheney.",
    h1: "Cheney Gutter Cleaning.",
    hook: "Wind blown debris cleared and flushed.",
  },
  "nine-mile-falls-gutter-cleaning": {
    title: "Nine Mile Falls Gutter Cleaning | Multi Plane Roofs, Full Flush",
    meta: "Nine Mile Falls gutter cleaning. Multi plane roofs, dormers, and porch runs cleared by hand. Downspouts flushed. Serving Suncrest and Long Lake shoreline.",
    h1: "Nine Mile Falls Gutter Cleaning.",
    hook: "Multi plane cleaning and full flush.",
  },
  // ------ GUTTER GUARDS ------
  "spokane-gutter-guards": {
    title: "Spokane Gutter Guards | Micro Mesh, Pine Needle Proof",
    meta: "Spokane gutter guards. Stainless .020 micro mesh sheds pine needles instead of packing them. Screw fastened, not clipped. Fits existing 5 and 6 inch gutters.",
    h1: "Spokane Gutter Guards.",
    hook: ".020 stainless micro mesh, screw fastened.",
  },
  "spokane-valley-gutter-guards": {
    title: "Spokane Valley Gutter Guards | Micro Mesh for Long Runs",
    meta: "Spokane Valley gutter guards. Stainless micro mesh installed continuously across long Valley ranch runs. Guards installed on the same day as install or clean.",
    h1: "Spokane Valley Gutter Guards.",
    hook: "Continuous mesh across long Valley runs.",
  },
  "liberty-lake-gutter-guards": {
    title: "Liberty Lake Gutter Guards | Cottonwood Proof Micro Mesh",
    meta: "Liberty Lake gutter guards. Cottonwood fluff and pine needle proof stainless micro mesh. Ideal for lakeside homes with heavy seasonal debris load.",
    h1: "Liberty Lake Gutter Guards.",
    hook: "Cottonwood proof stainless mesh.",
  },
  "mead-gutter-guards": {
    title: "Mead Gutter Guards | Ponderosa Needle Shedding Mesh",
    meta: "Mead gutter guards. Ponderosa pine needle load is the reason to install .020 stainless micro mesh at the right pitch. Serving Green Bluff and Colbert.",
    h1: "Mead Gutter Guards.",
    hook: "Ponderosa needle shedding mesh.",
  },
  "cheney-gutter-guards": {
    title: "Cheney Gutter Guards | Palouse Wind Proof Micro Mesh",
    meta: "Cheney gutter guards. Palouse wind carries dust and organic debris that clogs cheap screens. Stainless micro mesh sheds it. Serving all of Cheney WA.",
    h1: "Cheney Gutter Guards.",
    hook: "Wind proof micro mesh, screw fastened.",
  },
  "nine-mile-falls-gutter-guards": {
    title: "Nine Mile Falls Gutter Guards | Pine and Snow Load Mesh",
    meta: "Nine Mile Falls gutter guards. Pine load and heavy snow demand stainless micro mesh screw fastened at the correct pitch. Serving Suncrest and Long Lake.",
    h1: "Nine Mile Falls Gutter Guards.",
    hook: "Pine and snow load ready mesh.",
  },
  // ------ ROOFING ------
  "spokane-roofing": {
    title: "Spokane Roofing Contractor | Asphalt and Standing Seam Metal",
    meta: "Spokane roofing contractor. Architectural asphalt and 24 gauge standing seam metal roofs engineered for Spokane snow load and 130 mph wind. Drone inspection.",
    h1: "Spokane Roofing Contractor.",
    hook: "Asphalt and standing seam. Drone inspection.",
  },
  "spokane-valley-roofing": {
    title: "Spokane Valley Roofing | Ranch Roofs and Metal",
    meta: "Spokane Valley roofing. Ranch roof tear offs done in a day. Architectural asphalt and 24 gauge standing seam options. Serving Ponderosa and Greenacres.",
    h1: "Spokane Valley Roofing.",
    hook: "Ranch tear off, dry in same day.",
  },
  "liberty-lake-roofing": {
    title: "Liberty Lake Roofing | Modern Metal and Architectural Asphalt",
    meta: "Liberty Lake roofing. Standing seam metal in bronze or graphite for modern lake homes, plus architectural asphalt with 130 mph wind rating. Ice and water shield.",
    h1: "Liberty Lake Roofing.",
    hook: "Modern metal, ice and water shield.",
  },
  "mead-roofing": {
    title: "Mead Roofing | Snow Load and Fire Rated Underlayment",
    meta: "Mead roofing. Ponderosa country needs fire rated underlayment and snow load engineered rafter fastening. Full roof inspection with drone photos.",
    h1: "Mead Roofing.",
    hook: "Snow load and fire rated build.",
  },
  "cheney-roofing": {
    title: "Cheney Roofing Contractor | High Wind Rated Assemblies",
    meta: "Cheney roofing contractor. Palouse wind means 130 mph rated architectural asphalt and stud line metal fastening. Full manufacturer warranty. Free inspection.",
    h1: "Cheney Roofing.",
    hook: "130 mph wind rated assemblies.",
  },
  "nine-mile-falls-roofing": {
    title: "Nine Mile Falls Roofing | Metal Snow Shed and Ice Shield",
    meta: "Nine Mile Falls roofing. Metal snow shed engineered with snow guards over gutter runs. Ice and water shield on eaves and valleys. Serving Suncrest and Tumtum.",
    h1: "Nine Mile Falls Roofing.",
    hook: "Metal snow shed. Ice shield eaves.",
  },
};

// Combos array for iteration.
export const SERVICE_CITY_COMBOS = SERVICES.flatMap((s) =>
  CITIES.map((c) => ({
    slug: scSlug(c.slug, s.slug),
    serviceSlug: s.slug,
    citySlug: c.slug,
    keyword: `${c.name} ${s.name}`.toLowerCase(),
  }))
);

// Nearby city map used for "same service in nearby cities" cross-linking.
export const NEARBY = {
  spokane: ["spokane-valley", "mead", "liberty-lake"],
  "spokane-valley": ["spokane", "liberty-lake", "mead"],
  "liberty-lake": ["spokane-valley", "spokane", "mead"],
  mead: ["spokane", "nine-mile-falls", "spokane-valley"],
  cheney: ["spokane", "nine-mile-falls", "spokane-valley"],
  "nine-mile-falls": ["mead", "spokane", "cheney"],
};

// Sitemap page groups.
export const ALL_PAGES = () => {
  const pages = [];
  pages.push({ group: "Main", label: "Home", path: "/" });
  pages.push({ group: "Main", label: "About", path: "/about" });
  pages.push({ group: "Main", label: "Contact", path: "/contact" });
  SERVICES.forEach((s) => pages.push({ group: "Services", label: s.name, path: `/${s.slug}` }));
  CITIES.forEach((c) => pages.push({ group: "Service Areas", label: c.name, path: `/${c.slug}` }));
  SERVICES.forEach((s) =>
    CITIES.forEach((c) =>
      pages.push({
        group: "Service by City",
        label: `${c.name} ${s.name}`,
        path: `/${scSlug(c.slug, s.slug)}`,
      })
    )
  );
  return pages;
};
